import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusModal, NimbusModalContent } from './NimbusModal';
import { Dropzone } from './Dropzone';

interface Document {
  documentId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string | number;
  uploadedBy: string;
  uploadedByName?: string;
}

interface DocumentManagerProps {
  entityType: 'risk' | 'issue' | 'incident' | 'remediation' | 'threat' | 'evidence' | 'control' | 'asset' | 'interested_party' | 'physical_site' | 'legal_requirement' | 'security_objective' | 'ai_use_case' | 'subprocessor' | 'initiative_current_situation' | 'initiative_desired_situation';
  entityId: string;
  /** Renders the list/upload-button without the outer col-12/np-card/np-card-head - for a
   *  caller that wants attachments to read as part of ITS OWN card (e.g. Initiative's Current/
   *  Desired Situation cards) rather than as a second, visually separate card underneath.
   *  Upload/download/delete behaviour is identical either way; only the chrome differs. */
  embedded?: boolean;
  /** Download-only: hides Upload and Delete, keeps Download. For a caller whose managing
   *  (adding/removing files) happens elsewhere - e.g. Initiative's view page shows attachments
   *  read-only, since uploading is an edit action that belongs on its edit form, same as every
   *  other field on that page. */
  readOnly?: boolean;
  /** With embedded: no border-top wrapper and no "Attachments (N)" eyebrow - for a caller whose
   *  OWN np-card-head already titles the card (EvidenceRecordDetail). Default false = the
   *  current embedded chrome, unchanged for the Initiative pages. */
  bare?: boolean;
}

export const DocumentManager: React.FC<DocumentManagerProps> = ({ entityType, entityId, embedded, readOnly, bare }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const { data: documents = [], isLoading } = useQuery<Document[]>({
    queryKey: ['documents', entityType, entityId],
    queryFn: () => documentsApi.getAll(entityType, entityId),
    enabled: !!entityId,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => documentsApi.upload(entityType, entityId, file, setUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', entityType, entityId] });
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadProgress(0);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => documentsApi.delete(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', entityType, entityId] });
    },
  });

  const handleFileSelect = (files: File[]) => {
    const file = files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File size must be less than 25MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const handleDownload = (documentId: string, fileName: string) => {
    documentsApi.download(documentId, fileName);
  };

  const handleDelete = async (documentId: string, fileName: string) => {
    const confirmed = await swal.confirmDelete(fileName);
    if (confirmed) {
      deleteMutation.mutate(documentId);
    }
  };

  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'ri-file-pdf-line text-danger';
    if (type.includes('image') || type.includes('png') || type.includes('jpg')) return 'ri-image-line text-primary';
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xlsx')) return 'ri-file-excel-line text-success';
    if (type.includes('word') || type.includes('document') || type.includes('docx')) return 'ri-file-word-line text-info';
    return 'ri-file-line text-secondary';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const listContent = isLoading ? (
    <div className="text-center np-sub py-4">
      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
      <p className="mt-2">Loading documents...</p>
    </div>
  ) : documents.length === 0 ? (
    <div className="text-center np-sub py-4">
      <i className="ri-file-forbid-line" style={{ fontSize: '2rem' }}></i>
      <p className="mt-2">No documents attached</p>
    </div>
  ) : (
    <div className="np-list">
      {documents.map((doc) => (
        <div
          key={doc.documentId}
          className="np-list-item"
        >
          <div className="d-flex align-items-center flex-grow-1">
            <i className={`${getFileIcon(doc.fileType)} me-3`} style={{ fontSize: '1.5rem' }}></i>
            <div>
              <div className="fw-semibold">{doc.fileName}</div>
              <small className="np-sub np-tabular">
                {formatFileSize(doc.fileSize)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                {(doc.uploadedByName || doc.uploadedBy) && (
                  <> • <i className="ri-user-line me-1"></i>{doc.uploadedByName || doc.uploadedBy}</>
                )}
              </small>
            </div>
          </div>
          <div className="d-flex gap-1">
            <button
              className="np-icon-btn"
              onClick={() => handleDownload(doc.documentId, doc.fileName)}
              title="Download"
            >
              <i className="ri-download-line"></i>
            </button>
            {!readOnly && (
              <button
                className="np-icon-btn np-icon-btn-danger"
                onClick={() => handleDelete(doc.documentId, doc.fileName)}
                title="Delete"
                disabled={deleteMutation.isPending}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className={bare ? undefined : 'mt-3 pt-3 border-top'}>
          {(!bare || !readOnly) && (
            <div className="d-flex align-items-center gap-2 mb-2">
              {!bare && (
                <>
                  <i className="ri-attachment-line np-sub"></i>
                  <span className="eyebrow-label mb-0">Attachments{documents.length > 0 ? ` (${documents.length})` : ''}</span>
                </>
              )}
              {!readOnly && (
                <button
                  className="np-btn np-btn-ghost np-btn-sm ms-auto"
                  onClick={() => setShowUploadModal(true)}
                >
                  <i className="ri-upload-line"></i>
                  Upload File
                </button>
              )}
            </div>
          )}
          {listContent}
        </div>
      ) : (
        <div className="col-12 mb-4">
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-attachment-line"></i> Attachments</h2>
              {!readOnly && (
                <button
                  className="np-btn np-btn-primary np-btn-sm"
                  onClick={() => setShowUploadModal(true)}
                >
                  <i className="ri-upload-line"></i>
                  Upload File
                </button>
              )}
            </div>
            <div className="np-card-body">
              {listContent}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <NimbusModal centered>
          <NimbusModalContent
            title="Upload Document"
            onClose={() => setShowUploadModal(false)}
            footer={
              <>
                <button
                  type="button"
                  className="np-btn np-btn-ghost"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploadMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </button>
              </>
            }
          >
            <div className="np-field">
              <label>Select File</label>
              <Dropzone
                onFilesSelected={handleFileSelect}
                multiple={false}
                disabled={uploadMutation.isPending}
                accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx,.doc,.xls,.csv,.txt"
                hint="Max 25MB. Allowed: PDF, PNG, JPG, XLSX, DOCX, CSV, TXT"
              />
              {selectedFile && (
                <div className="mt-2 np-tabular">
                  <strong>Selected:</strong> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
              )}
            </div>
            {uploadMutation.isPending && (
              <div>
                <div className="np-progress">
                  <div
                    className="np-fill"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-center mt-2 mb-0 np-sub np-tabular">
                  <small>Uploading... {uploadProgress}%</small>
                </p>
              </div>
            )}
            {uploadMutation.isError && (
              <div className="np-banner np-tone-danger" role="alert">
                <i className="ri-error-warning-line"></i>
                Upload failed. Please try again.
              </div>
            )}
          </NimbusModalContent>
        </NimbusModal>
      )}
    </>
  );
};
